import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { AgentChatAttachment, AgentChatMessage, AgentChatbot } from "capitalrx-components-2.0/lib/agent";
import { MarkdownContent } from "capitalrx-components-2.0/lib/components/MarkdownContent";
import { createChatSession, createJarvisFetch, getChatHistory, sendMessageStream } from "capitalrx-jarvis/lib/api";
import { buildAdditionalContext } from "capitalrx-jarvis/lib/context";

import { useAuthToken } from "../hooks";
import { PAGE_AGENT_CHAT_OPT_IN_KEY, PAGE_SNAPSHOT_OPT_IN_KEY, useAgentChatPageContext } from "./AgentChatContext";
import { useHasQueryDevtoolsButton } from "./useHasQueryDevtoolsButton";
import { useAgentChatBaseUrl, useIsAgentChatEnabled } from "./useIsAgentChatEnabled";

/** Key used to remember the active chat session across page reloads. */
const CHAT_SESSION_STORAGE_KEY = "capitalrx.agentChatbot.chatSessionId";

/** Key used to remember that the disclaimer was dismissed. Outlives any single chat session. */
const DISCLAIMER_DISMISSED_STORAGE_KEY = "capitalrx.agentChatbot.disclaimerDismissed";

/* Space reserved at the bottom of the dock so the panel clears a collapsed `TaskFlyout`. Kept as a
   bare sub-expression: the reservations are joined and wrapped in a single `calc()` below. */
const TASK_FLYOUT_RESERVED_SPACE = "var(--task_flyout-dimension-collapsed_height, 52px) + var(--task_flyout-spacing-bottom, 1rem)";

/** Space reserved so the dock clears the TanStack Query devtools launcher: its button plus its inset. */
const QUERY_DEVTOOLS_RESERVED_SPACE = "56px";

/** Query param testers use to pick an agent persona, e.g. `?jarvis_persona=MemberPortalAgent`. */
const PERSONA_QUERY_PARAM = "jarvis_persona";

const GENERIC_ERROR_MESSAGE = "Unable to connect to Judi's AI service";

/* File attachments are not wired to a backend yet (send-message ignores them); hide the attach
   affordance entirely so picked or dropped files can't silently vanish on send.
   See capitalrx-components-2.0/lib/agent/README.md "Known gap". */
const NO_ATTACHMENTS: ReadonlyArray<AgentChatAttachment> = [];

/** A conversation entry held in its raw text form so streamed chunks can be appended. */
interface ChatEntry {
  readonly id: string;
  readonly role: "user" | "assistant";
  readonly text: string;
}

/** The `AgentChatbotFlyout` component properties. */
export interface AgentChatbotFlyoutProps {
  /** Indicates whether the task flyout is currently occupying the bottom-right corner. */
  readonly hasTasks?: boolean;
}

/**
 * Shell adapter for the shared `AgentChatbot`. Owns the Jarvis chat session, message list, and
 * streaming lifecycle so any page in the shell gets a fully wired assistant without doing its own
 * API work. Pages contribute identifiers through `useAgentChatContext`.
 */
export const AgentChatbotFlyout: FC<AgentChatbotFlyoutProps> = ({ hasTasks = false }) => {
  const [authToken] = useAuthToken();
  const pageContext = useAgentChatPageContext();

  const [isOpen, setIsOpen] = useState(false);
  const [entries, setEntries] = useState<ReadonlyArray<ChatEntry>>([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [toolStatusLabel, setToolStatusLabel] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isDisclaimerDismissed, setIsDisclaimerDismissed] = useState(() => {
    try {
      return window.sessionStorage.getItem(DISCLAIMER_DISMISSED_STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });

  const chatSessionIdRef = useRef<string | null>(null);
  const parentMessageIdRef = useRef<number | null>(null);
  const lastMessageRef = useRef<string>("");
  const abortRef = useRef<AbortController | null>(null);
  const entryCountRef = useRef(0);
  const hasLoadedHistoryRef = useRef(false);
  const isMountedRef = useRef(true);

  const baseUrl = useAgentChatBaseUrl();
  const isEnabled = useIsAgentChatEnabled();
  const hasQueryDevtoolsButton = useHasQueryDevtoolsButton();

  /* The dock shares the bottom-right corner with the task flyout and, in non-production builds, the
     query devtools launcher; it has to clear whichever of them are on screen. */
  const reservedBottomSpace = useMemo(() => {
    const reservations: string[] = [];
    if (hasTasks) reservations.push(TASK_FLYOUT_RESERVED_SPACE);
    if (hasQueryDevtoolsButton) reservations.push(QUERY_DEVTOOLS_RESERVED_SPACE);

    if (reservations.length === 0) return undefined;

    return `calc(${reservations.join(" + ")})`;
  }, [hasQueryDevtoolsButton, hasTasks]);

  /* Read once per mount: the persona only matters when a session is created, and testers set it in
     the address bar before opening the panel. */
  const personaName = useMemo(() => {
    const value = new URLSearchParams(window.location.search).get(PERSONA_QUERY_PARAM);
    return value ?? undefined;
  }, []);

  const jarvisFetch = useMemo(
    () => createJarvisFetch(baseUrl, authToken ?? undefined),
    [authToken, baseUrl],
  );

  const messages = useMemo<ReadonlyArray<AgentChatMessage>>(() => {
    return entries.map((entry) => {
      if (entry.role === "user") {
        return { id: entry.id, role: entry.role, content: entry.text };
      }

      return { id: entry.id, role: entry.role, content: <MarkdownContent content={entry.text} /> };
    });
  }, [entries]);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  const nextEntryId = useCallback((prefix: string): string => {
    entryCountRef.current += 1;
    return `${prefix}-${entryCountRef.current}`;
  }, []);

  const appendEntry = useCallback((entry: ChatEntry) => {
    setEntries((previous) => [...previous, entry]);
  }, []);

  const appendToEntry = useCallback((entryId: string, chunk: string) => {
    setEntries((previous) => previous.map((entry) => {
      if (entry.id !== entryId) return entry;
      return { ...entry, text: entry.text + chunk };
    }));
  }, []);

  const readStoredSessionId = useCallback((): string | null => {
    try {
      return window.sessionStorage.getItem(CHAT_SESSION_STORAGE_KEY);
    } catch {
      return null;
    }
  }, []);

  const storeSessionId = useCallback((chatSessionId: string) => {
    try {
      window.sessionStorage.setItem(CHAT_SESSION_STORAGE_KEY, chatSessionId);
    } catch {
      /* Session storage is unavailable; the session simply will not survive a reload. */
    }
  }, []);

  const clearStoredSessionId = useCallback(() => {
    try {
      window.sessionStorage.removeItem(CHAT_SESSION_STORAGE_KEY);
    } catch {
      /* Session storage is unavailable; nothing to clean up. */
    }
  }, []);

  const ensureSession = useCallback(async (): Promise<string> => {
    if (chatSessionIdRef.current) return chatSessionIdRef.current;

    const chatSessionId = await createChatSession({ fetchFn: jarvisFetch, personaName });
    chatSessionIdRef.current = chatSessionId;
    storeSessionId(chatSessionId);

    return chatSessionId;
  }, [jarvisFetch, personaName, storeSessionId]);

  const loadHistory = useCallback(async (chatSessionId: string) => {
    const history = await getChatHistory({ fetchFn: jarvisFetch, chatSessionId });
    if (!isMountedRef.current || history.messages.length === 0) return;

    entryCountRef.current += history.messages.length;
    setEntries(history.messages.map((message) => ({
      id: message.id,
      role: message.role,
      text: message.content,
    })));

    const lastMessage = history.messages[history.messages.length - 1];
    parentMessageIdRef.current = lastMessage ? lastMessage.messageId : null;
  }, [jarvisFetch]);

  /* Restore a conversation started before a reload the first time the panel is opened. */
  useEffect(() => {
    if (!isOpen || hasLoadedHistoryRef.current) return;

    hasLoadedHistoryRef.current = true;

    const storedSessionId = readStoredSessionId();
    if (!storedSessionId) return;

    chatSessionIdRef.current = storedSessionId;
    setIsLoading(true);

    loadHistory(storedSessionId)
      .catch(() => {
        /* A stale session is not worth surfacing; start clean instead. */
        chatSessionIdRef.current = null;
        clearStoredSessionId();
      })
      .finally(() => {
        if (isMountedRef.current) setIsLoading(false);
      });
  }, [clearStoredSessionId, isOpen, loadHistory, readStoredSessionId]);

  const startAssistantEntry = useCallback((entryId: string) => {
    setEntries((previous) => {
      /* A prior attempt for this same message can leave an assistant bubble behind (empty, or
         partially streamed before failing); reuse its slot on retry instead of appending a second
         reply. */
      const lastEntry = previous[previous.length - 1];
      const withoutPreviousReply = lastEntry && lastEntry.role === "assistant"
        ? previous.slice(0, -1)
        : previous;

      return [...withoutPreviousReply, { id: entryId, role: "assistant" as const, text: "" }];
    });
  }, []);

  /* A failed attempt can leave an empty assistant bubble behind. `startAssistantEntry` reclaims it
     on retry, but it stops being the trailing entry once the user sends a new message instead, so
     drop it here. */
  const dropEmptyAssistantEntry = useCallback(() => {
    setEntries((previous) => {
      const lastEntry = previous[previous.length - 1];
      if (!lastEntry || lastEntry.role !== "assistant" || lastEntry.text) return previous;

      return previous.slice(0, -1);
    });
  }, []);

  const streamAnswer = useCallback(async (message: string) => {
    const assistantEntryId = nextEntryId("assistant");
    startAssistantEntry(assistantEntryId);

    setIsLoading(true);
    setToolStatusLabel(null);
    setErrorMessage(null);

    const abortController = new AbortController();
    abortRef.current = abortController;

    /* Answer pieces arrive faster than they are worth rendering, so they are buffered and flushed
       once per animation frame instead of triggering a render per token. */
    let bufferedText = "";
    let frameId: number | null = null;

    const flushBuffer = () => {
      frameId = null;
      if (!bufferedText) return;

      const chunk = bufferedText;
      bufferedText = "";
      appendToEntry(assistantEntryId, chunk);
    };

    const scheduleFlush = () => {
      if (frameId !== null) return;

      if (typeof window === "undefined" || typeof window.requestAnimationFrame !== "function") {
        flushBuffer();
        return;
      }

      frameId = window.requestAnimationFrame(flushBuffer);
    };

    const cancelScheduledFlush = () => {
      if (frameId === null) return;

      if (typeof window !== "undefined" && typeof window.cancelAnimationFrame === "function") {
        window.cancelAnimationFrame(frameId);
      }
      frameId = null;
    };

    try {
      const chatSessionId = await ensureSession();

      /* the page snapshot scrapes rendered DOM text, so only pages that explicitly opt in send it;
         the opt-in flags themselves are never forwarded to the agent. */
      const {
        [PAGE_SNAPSHOT_OPT_IN_KEY]: isPageSnapshotEnabled,
        [PAGE_AGENT_CHAT_OPT_IN_KEY]: _isAgentChatPageEnabled,
        ...publishedContext
      } = pageContext;
      const additionalContext = buildAdditionalContext(
        publishedContext,
        { isPageSnapshotEnabled: isPageSnapshotEnabled === true },
      );

      for await (const packet of sendMessageStream({
        fetchFn: jarvisFetch,
        chatSessionId,
        message,
        parentMessageId: parentMessageIdRef.current,
        signal: abortController.signal,
        additionalContext,
      })) {
        if (Object.prototype.hasOwnProperty.call(packet, "reserved_assistant_message_id")) {
          parentMessageIdRef.current = packet["reserved_assistant_message_id"] as number;
        }

        /* The service names the step it is on ("Looking up claim details…"); show the latest one
           beside the thinking dots until the answer itself starts arriving. */
        if (Object.prototype.hasOwnProperty.call(packet, "tool_display_name")) {
          const displayName = packet["tool_display_name"];
          setToolStatusLabel(typeof displayName === "string" && displayName ? displayName : null);
        }

        if (Object.prototype.hasOwnProperty.call(packet, "answer_piece")) {
          setToolStatusLabel(null);
          bufferedText += String(packet["answer_piece"] ?? "");
          scheduleFlush();
        }

        /* The service sends `StreamingError`, whose text lives on `error`; `message` is only
           present on the plain JSON error bodies the gateway returns. */
        if (Object.prototype.hasOwnProperty.call(packet, "error")) {
          const detail = packet["error"] ?? packet["message"];
          throw new Error(String(detail || GENERIC_ERROR_MESSAGE));
        }
      }

      cancelScheduledFlush();
      flushBuffer();
    } catch (error) {
      const wasAborted = abortController.signal.aborted;

      /* The stream was abandoned mid-iteration, so cancel the request; otherwise its response body
         stays locked and the connection is held open until the server gives up. */
      abortController.abort();

      cancelScheduledFlush();
      flushBuffer();

      if (!isMountedRef.current || wasAborted) return;

      setErrorMessage(error instanceof Error ? error.message : String(error));
    } finally {
      abortRef.current = null;
      if (isMountedRef.current) {
        setIsLoading(false);
        setToolStatusLabel(null);
      }
    }
  }, [appendToEntry, ensureSession, jarvisFetch, nextEntryId, pageContext, startAssistantEntry]);

  const handleOpen = useCallback(() => setIsOpen(true), []);

  const handleClose = useCallback(() => setIsOpen(false), []);

  const handleDisclaimerDismiss = useCallback(() => {
    setIsDisclaimerDismissed(true);

    try {
      window.sessionStorage.setItem(DISCLAIMER_DISMISSED_STORAGE_KEY, "true");
    } catch {
      /* Session storage is unavailable; the disclaimer simply returns on the next page load. */
    }
  }, []);

  /* Drop the transcript and the session it belongs to; the next message starts a fresh one. */
  const handleClear = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;

    chatSessionIdRef.current = null;
    parentMessageIdRef.current = null;
    lastMessageRef.current = "";
    hasLoadedHistoryRef.current = true;
    clearStoredSessionId();

    setEntries([]);
    setInputValue("");
    setIsLoading(false);
    setToolStatusLabel(null);
    setErrorMessage(null);

    /* A new conversation gets the advisory again, so an earlier dismissal is forgotten. */
    setIsDisclaimerDismissed(false);
    try {
      window.sessionStorage.removeItem(DISCLAIMER_DISMISSED_STORAGE_KEY);
    } catch {
      /* Session storage is unavailable; nothing to clean up. */
    }
  }, [clearStoredSessionId]);

  const handleSubmit = useCallback(() => {
    const trimmedInput = inputValue.trim();
    if (!trimmedInput || isLoading) return;

    setInputValue("");
    lastMessageRef.current = trimmedInput;
    dropEmptyAssistantEntry();
    appendEntry({ id: nextEntryId("user"), role: "user", text: trimmedInput });

    void streamAnswer(trimmedInput);
  }, [appendEntry, dropEmptyAssistantEntry, inputValue, isLoading, nextEntryId, streamAnswer]);

  const handleRetry = useCallback(() => {
    if (!lastMessageRef.current || isLoading) return;

    setErrorMessage(null);
    void streamAnswer(lastMessageRef.current);
  }, [isLoading, streamAnswer]);

  if (!isEnabled) return null;

  return (
    <AgentChatbot
      layout="standalone"
      dock="bottom-right"
      reservedBottomSpace={reservedBottomSpace}
      open={isOpen}
      onOpen={handleOpen}
      onClose={handleClose}
      onClear={handleClear}
      /* `undefined` keeps the component's own default copy; `null` suppresses it once dismissed. */
      disclaimer={isDisclaimerDismissed ? null : undefined}
      onDisclaimerDismiss={handleDisclaimerDismiss}
      messages={messages}
      isLoading={isLoading}
      loadingStatusLabel={toolStatusLabel}
      hasError={Boolean(errorMessage)}
      errorMessage={errorMessage ?? GENERIC_ERROR_MESSAGE}
      onRetry={handleRetry}
      attachedFiles={NO_ATTACHMENTS}
      isAttachmentEnabled={false}
      inputValue={inputValue}
      onInputChange={setInputValue}
      onSubmit={handleSubmit}
    />
  );
};
